import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import { useLanguage } from '../../../../Context/LanguageContext';
import { useTheme } from '../../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../../Styles/theme';

import HoldingAndExtendHistoryInfoView from './HoldingAndExtendHistoryInfoView';
import RefundInfoView from './RefundInfoView';

import OrangeTrashIcon from '../../../../image/OrangeTrashIcon.png';
import { ReactComponent as MenuSvg } from '../../../../image/Menu.svg';

const MemberShipHistoryInfoView = forwardRef((props, ref) => {
  const { t } = useLanguage();
  const {
    data, 
    index, 
    size,

    firstExpiredIndex,

    modifyClick, 
    holdingClick, 
    extensionClick, 
    refundClick,
    assignClick,
    subscriptionCancelClick,
    deleteClick,

    onClickHoldingAndExtendButton, 
    onClickHoldingEdit,
    onClickDeleteHoldingHistory,
    onClickModifyExtend,
    onClickDeleteExtend,
    onClickRefundDelete,
    onClickDeleteExpectedUsage
  } = props;
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [isClicked, setIsClicked] = useState(false);
  const [fontColor, setFontColor] = useState(theme.text);
  const [border, setBorder] = useState(`1px solid ${theme.subBackground}`);
  const [hoverBorder, setHoverBorder] = useState(`1px solid ${theme.subBackground}`);
  const [backgroundColor, setBackgroundColor] = useState(theme.subBackground);

  const OptionBoxRef = useRef();
  const OptionButtonRef = useRef();
  const OptionEditRef = useRef();
  const OptionHoldRef = useRef();
  const OptionExtendRef = useRef();
  const OptionRefundRef = useRef();
  const OptionAssignRef = useRef();
  const OptionDeleteRef = useRef();
  const SubscriptionCancelRef = useRef();
  
  const ExpectedDeleteRef = useRef();

  useImperativeHandle(ref, () => ({
    setIsClicked: () => {
      setIsClicked(false);
    },
    getButtonRef: () => {
      return OptionButtonRef.current;
    },
    getBoxRef: () => {
      return OptionBoxRef.current;
    }
  }))

  useEffect(() => {
    if (isClicked) {
      let optionCount;
      if (data.isExpectedMemberShip) optionCount = 2;
      else {
        const isAssignVisible = !moment().isAfter(moment(data.endDate), 'day') && data.transferDirection !== 'OUT';
        optionCount = 5 + (isAssignVisible ? 1 : 0) + (data.isSubscriptionMembership ? 1 : 0);
      }
      OptionBoxRef.current.style.height = `${optionCount * 38}px`;
      OptionBoxRef.current.style.border = `1px solid ${theme.primary}`;
    }
    else {
      OptionBoxRef.current.style.height = `0px`;
      OptionBoxRef.current.style.border = `0px solid ${theme.primary}`;
    }
  }, [isClicked, data, theme])

  useEffect(() => {
    document.addEventListener("mousedown", onClickOutSide);

    return () => {
      document.removeEventListener("mousedown", onClickOutSide);
    }
  }, [])

  useEffect(() => {
    const startDate = moment(data.startDate);
    const endDate = moment(data.endDate);
    
    // 환불 데이터의 경우 //
    if (data.isRefund) {
      setFontColor(theme.text);
      setBorder(`1px solid ${theme.buttonFill}`);
      setHoverBorder(`1px solid ${theme.primary}`);
      setBackgroundColor(theme.buttonFill);
    }
    else if (data.isExpectedMemberShip) {
      setFontColor(theme.text);
      setBorder(`1px solid ${theme.subBackground}`);
      setHoverBorder(`1px solid ${theme.primary}`);
      setBackgroundColor(theme.subBackground);
    }
    // 사용 예정의 데이터인 경우 //
    else if (moment().isBefore(startDate)) {
      setFontColor((data.notReceivedPrice ?? 0) > 0 ? theme.pointRed : theme.text);
      setBorder(`1px solid ${theme.subBackground}`);
      setHoverBorder(`1px solid ${theme.primary}`);
      setBackgroundColor(theme.subBackground);
    }
    // 사용 기한이 끝나지 않은 경우 //
    else if (moment().isBefore(endDate)) {
      if (data.totalMax !== -1
        && data.totalMax <= data.courseTakeCount
        && (data.lastReserveDate !== undefined && moment().startOf('day').isAfter(data.lastReserveDate))) {
        setFontColor((data.notReceivedPrice ?? 0) > 0 ? theme.pointRed : theme.text);
        setBorder(`1px solid ${theme.buttonFill}`);
        setHoverBorder(`1px solid ${theme.primary}`);
        setBackgroundColor(theme.buttonFill);
      }
      else {
        setFontColor((data.notReceivedPrice ?? 0) > 0 ? theme.pointRed : theme.text);
        setBorder(`1px solid ${theme.subBackground}`);
        setHoverBorder(`1px solid ${theme.primary}`);
        setBackgroundColor(theme.subBackground);
      }
    }
    // 예정된 기한이 지난경우 //
    else {
      setFontColor((data.notReceivedPrice ?? 0) > 0 ? theme.pointRed : theme.text);
      setBorder(`1px solid ${theme.buttonFill}`);
      setHoverBorder(`1px solid ${theme.primary}`);
      setBackgroundColor(theme.buttonFill);
    }
  }, [data, theme, isDark])

  // 구독 상태 결정 //
  function decideSubscriptionStatus() {
    if (data.subscribeStatus && !data.subscribeDeleted) return 'subscribing';

    // 결제 대기(링코페이 결제 요청 등)는 시작 예정보다 우선 //
    if (data.isExpectedMemberShip) return 'waiting';

    if (moment().isBefore(moment(data.startDate))) return 'expected';

    return 'ended';
  }

  function onClickOutSide(e) {
    if (OptionBoxRef.current !== e.target
      && OptionButtonRef.current !== e.target
      && OptionEditRef.current !== e.target
      && OptionHoldRef.current !== e.target
      && OptionExtendRef.current !== e.target
      && OptionRefundRef.current !== e.target
      && OptionAssignRef.current !== e.target
      && SubscriptionCancelRef.current !== e.target
      && OptionDeleteRef.current !== e.target) {
      setIsClicked(false);
    }
  }

  function mouseOver() {
    OptionButtonRef.current.style.opacity = 1;
  }

  function mouseLeave() {
    OptionButtonRef.current.style.opacity = 0;
  }

  // 링코페이 여부 확인 //
  function isLinkoPay() {
    if (!data.merchantUid) return false;
    const indicator = data.merchantUid.split('-')[2];
    return indicator === '1' || indicator === '2';
  }

  // 결제 금액 정리 //
  function decideTotalPriceView() {
    if (data.selectedPaymentOptions === undefined || data.selectedPaymentOptions.length === 0) {
      if (data.cardPrice === 0 && data.cashPrice === 0) return t('memberDetail.payFree');
      else if (data.cardPrice === 0) return data.cashPrice.toLocaleString('ko-KR') + ' - ' + t('memberDetail.payCash');
      else if (data.cashPrice === 0) return data.cardPrice.toLocaleString('ko-KR') + ' - ' + (isLinkoPay() ? t('memberDetail.payLinkoPay') : t('memberDetail.payCard'));
      else return (parseInt(data.cashPrice) + parseInt(data.cardPrice)).toLocaleString('ko-KR') + ' - ' + t('memberDetail.payCombined');
    }
    else {
      const allSame = data.selectedPaymentOptions.every(item => item.name === data.selectedPaymentOptions[0].name);
      const totalPrice = data.selectedPaymentOptions.reduce((sum, item) => sum + item.price, 0);

      const paymentNameMap = { '카드': isLinkoPay() ? t('memberDetail.payLinkoPay') : t('memberDetail.payCard'), '현금': t('memberDetail.payCash'), '포인트': t('memberDetail.payPoint'), '링코페이': t('memberDetail.payLinkoPay') };

      if (allSame) return totalPrice.toLocaleString('ko-KR') + ' - ' + (paymentNameMap[data.selectedPaymentOptions[0].name] || data.selectedPaymentOptions[0].name);
      else return totalPrice.toLocaleString('ko-KR') + ' - ' + t('memberDetail.payCombined');
    }
  }

  // 결제 정보를 가격 / 결제수단 으로 분해 //
  function splitPaymentInfo() {
    const result = decideTotalPriceView();
    const dashIdx = result.indexOf(' - ');

    if (dashIdx === -1) return { priceText: result, methodText: '' };

    return {
      priceText: result.slice(0, dashIdx),
      methodText: result.slice(dashIdx).replace(' - ', '')
    };
  }

  // 결제 금액 + 결제 수단 셀 렌더링 //
  function renderPaymentCell() {
    const { priceText, methodText } = splitPaymentInfo();

    if (!methodText) return <Text color={fontColor}>{priceText}</Text>;

    return (
      <PaymentTextWrapper>
        <Text color={fontColor} style={{ flexShrink: 0 }}>{priceText}</Text>
        <PaymentMethodText color={fontColor}>{' - ' + methodText}</PaymentMethodText>
      </PaymentTextWrapper>
    );
  }

  // 남은일수에 맞춰 텍스트 정리 //
  function remainDaysView() {
    if (data.isRefund) return t('memberDetail.statusRefund');
    else if (data.isExpectedMemberShip) {
      const days = moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'days');

      if (days > 0) return days + t('memberDetail.daysSuffix');

      if (days === 0) return 'D-Day';
      return t('memberDetail.statusExpired');
    }
    else if (data.totalMax === data.courseTakeCount
      && (data.lastReserveDate !== undefined && (moment().startOf('day').isAfter(data.lastReserveDate)))) return t('memberDetail.statusExpired');
    else if (moment().isBefore(data.startDate)) {
      const days = moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'days');
      if (days > 0) return days + t('memberDetail.daysSuffix');
      if (days === 0) return 'D-Day';
      return t('memberDetail.statusExpired');
    }
    else if (moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'days') > 0) {
      return moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'days') + t('memberDetail.daysSuffix');
    }
    else if (moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'days') === 0) {
      return 'D-Day'
    }

    return t('memberDetail.statusExpired');
  }

  // 수강 횟수 표시 //
  function decideUseTotalMax() {
    if (data.totalMax === -1) return data.courseTakeCount + '/∞';
    else return data.courseTakeCount + '/' + data.totalMax;
  }
  

  // 몸체 눌렀을때 켜지기 //
  function onClickBody(e) {
    if (OptionButtonRef.current.contains(e.target) || OptionBoxRef.current.contains(e.target)) return;

    modifyClick(index);
  }

  return (
    <TopLevelWrapper>
      { (firstExpiredIndex !== -1 && firstExpiredIndex === index) && <ExpiredDivisionLine/> }
      <MemberShipComponent 
        $isDark={isDark}
        zIndex={60 + size - index}
        border={border} 
        hoverBorder={hoverBorder} 
        backgroundColor={backgroundColor} 
        isPartial={data.isRefund || data.historyData.length > 0 || (data.transferId != null && data.transferDirection === 'OUT')}
        onMouseOver={mouseOver} 
        onMouseLeave={mouseLeave} 
        onClick={onClickBody}>
      <Table>
        <Body>
          <Tr style={{ width: '270px' }}>
            <Td>
              <TextWrapper style={{ maxWidth: '270px', gap: '4px' }}>
                {
                  (data.isSubscriptionMembership || data.isExpectedMemberShip || moment().isBefore(moment(data.startDate))) &&
                  <SubscriptionBox statusType={decideSubscriptionStatus()}>
                    <SubscriptionText>
                      {decideSubscriptionStatus() === 'subscribing' ? t('memberDetail.statusSubscribing')
                        : decideSubscriptionStatus() === 'waiting' ? t('memberDetail.statusWaiting')
                        : decideSubscriptionStatus() === 'expected' ? t('memberDetail.statusScheduled')
                        : t('memberDetail.statusSubscriptionEnded')}
                    </SubscriptionText>
                  </SubscriptionBox>
                }
                <Text color={fontColor}>{data.memberShipName}</Text>
              </TextWrapper>
            </Td>
          </Tr>
          <Tr style={{ width: '145px' }}>
            <Td>
              <TextWrapper style={{ maxWidth: '135px' }}>
                <Text color={fontColor}>
                  {moment(data.startDate).format(t('calendarInput.shortDateFormat'))} - {moment(data.endDate).format(t('calendarInput.shortDateFormat'))}
                </Text>
              </TextWrapper>
            </Td>
          </Tr>
          <Tr style={{ width: '190px' }}>
            <Td>
              <TextWrapper style={(data.transferId != null && (data.transferDirection === 'IN' || (data.transferDirection === 'OUT' && data.originalFromMemberName))) ? { maxWidth: '180px', maxHeight: 'none' } : { maxWidth: '180px' }}>
                {
                  (data.transferId != null && data.transferDirection === 'IN') ?
                    <TransferLineClampText color={fontColor}>
                      {t('memberDetail.transferReceivedFromName').replace('{name}', data.counterMemberName || t('memberDetail.transferCounterDeletedMember'))}
                    </TransferLineClampText>
                    :
                    (data.transferId != null && data.transferDirection === 'OUT' && data.originalFromMemberName) ?
                      <TransferLineClampText color={fontColor}>
                        {t('memberDetail.transferReceivedFromName').replace('{name}', data.originalFromMemberName)}
                      </TransferLineClampText>
                      :
                      renderPaymentCell()
                }
              </TextWrapper>
            </Td>
          </Tr>
          <Tr style={{ width: '80px' }}>
            <Td>
              <TextWrapper style={{ maxWidth: '70px' }}>
                <Text color={fontColor}>{data.notReceivedPrice.toLocaleString('ko-KR')}</Text>
              </TextWrapper>
            </Td>
          </Tr>
          <Tr style={{ width: '4px'}}/>
          <Tr style={{ width: '65px' }}>
            <Td>
              <TextWrapper style={{ maxWidth: '65px' }}>
                <Text color={fontColor}>{remainDaysView()}</Text>
              </TextWrapper>
            </Td>
          </Tr>
          <Tr style={{ width: '55px', marginRight: ''}}>
            <Td>
              <TextWrapper style={{ maxWidth: '55px' }}>
                <Text color={fontColor}>{decideUseTotalMax()}</Text>
              </TextWrapper>
            </Td>
          </Tr>
          <Tr>
              {
                data.isExpectedMemberShip ?
                  <Td>
                    <OptionButton ref={OptionButtonRef} onClick={() => setIsClicked(!isClicked)} />
                    <OptionWrapper ref={OptionBoxRef}>
                      <Option ref={OptionEditRef} onMouseDown={() => modifyClick(index)}>{t('memberDetail.btnRetryPayment')}</Option>
                      <Option ref={OptionDeleteRef} onMouseDown={() => onClickDeleteExpectedUsage(data)}>{t('memberDetail.btnDelete')}</Option>
                    </OptionWrapper>
                  </Td>
                  :
                  <Td>
                    <OptionButton ref={OptionButtonRef} onClick={() => setIsClicked(!isClicked)} />
                    <OptionWrapper ref={OptionBoxRef}>
                      <Option ref={OptionEditRef} onMouseDown={() => modifyClick(index)}>{t('memberDetail.btnModify')}</Option>
                      <Option ref={OptionHoldRef} onMouseDown={() => holdingClick(index)}>{t('memberDetail.btnHolding')}</Option>
                      <Option ref={OptionExtendRef} onMouseDown={() => extensionClick(index)}>{t('memberDetail.btnExtension')}</Option>
                      <Option ref={OptionRefundRef} onMouseDown={() => refundClick(index)}>{t('memberDetail.btnRefund')}</Option>
                      {
                        !moment().isAfter(moment(data.endDate), 'day') && data.transferDirection !== 'OUT' &&
                        <Option ref={OptionAssignRef} onMouseDown={() => assignClick(index, splitPaymentInfo().methodText)}>{t('memberDetail.btnAssign')}</Option>
                      }
                      { data.isSubscriptionMembership && <Option ref={SubscriptionCancelRef} onMouseDown={() => subscriptionCancelClick(index)}>{t('memberDetail.btnCancelSubscription')}</Option> }
                      <Option ref={OptionDeleteRef} onMouseDown={() => deleteClick(index)}>{t('memberDetail.btnDelete')}</Option>
                    </OptionWrapper>
                  </Td>
            }
          </Tr>
        </Body>
      </Table>
      </MemberShipComponent>
      <SubBackgroundCover zIndex={60 + size - index - 1}/>
      {
        data.isRefund &&
        <ExpertComponentWrapper zIndex={data.historyData.length + 1}>
          <RefundInfoView
            data={data}
            onClickDeleteRefund={onClickRefundDelete}
            onClickModifyRefund={() => refundClick(index)}/>
        </ExpertComponentWrapper>
      }
      {
        !data.isRefund && data.transferId != null && data.transferDirection === 'OUT' &&
        <ExpertComponentWrapper zIndex={data.historyData.length + 1}>
          <RefundInfoView
            data={data}
            mode='transferOut'
            counterMemberName={data.counterMemberName} />
        </ExpertComponentWrapper>
      }
      {
        data.historyData.map((innerData, index) => (
          <ExpertComponentWrapper key={'MembershipHistory-' + index} zIndex={data.historyData.length - index}>
            <HoldingAndExtendHistoryInfoView 
              data={innerData} 
              onClickHoldingAndExtendButton={onClickHoldingAndExtendButton} 
              onClickHoldingEdit={onClickHoldingEdit}
              onClickDeleteHoldingHistory={onClickDeleteHoldingHistory}
              onClickModifyExtend={onClickModifyExtend}
              onClickDeleteExtend={onClickDeleteExtend} />
          </ExpertComponentWrapper>
        ))
      }
    </TopLevelWrapper>
  )
});

export default MemberShipHistoryInfoView;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 970px;
`;

const MemberShipComponent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  width: 970px;
  height: 52px;

  z-index: ${props => props.zIndex};

  border: ${props => props.border};
  border-radius: ${props => props.isPartial ? '30px 10px 0px 30px' : '30px'};
  background-color: ${props => props.backgroundColor};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    border: ${props => props.hoverBorder};
  }
`;

const ExpiredDivisionLine = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  margin: 10px 0 20px;

  width: 935px;
  height: 1px;

  background-color: ${props => props.theme.line};
`;

const Table = styled.table`
  display: flex;
  flex-direction: row;
`;

const Body = styled.tbody`
  display: flex;
  flex-direction: row;

  align-items: center;
    
  margin-left: 15px;

  gap: 15px;
`;

const Tr = styled.tr`
  display: flex;
  flex-direction: row;
`;

const Td = styled.td`
  display: flex;
  flex-direction: row;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: flex-start;

  overflow: hidden;

  max-height: 20px;
`;

const Text = styled.span`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  padding: 0 0 0 0;
`;

const PaymentTextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: flex-start;
`;

const PaymentMethodText = styled.span`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const TransferLineClampText = styled.span`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  white-space: normal;
  line-height: 1.3;
`;

const SubscriptionBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 0px 5px;
  margin-top: 2px;

  min-width: 30px;
  min-height: 18px;

  border-radius: 5px;
  background-color: ${props =>
    props.statusType === 'subscribing' ? props.theme.primary
    : props.statusType === 'ended' ? props.theme.primaryAlpha50
    : props.statusType === 'expected' ? props.theme.pointYellow
    : props.theme.subKey};
`;

const SubscriptionText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 12px;

  color: ${props => props.theme.subBackground};

  margin: -2px 0 0 0;
  padding: 0 0 0 0;

  &:hover {
    cursor: default;
  }
`;

const OptionButton = styled(MenuSvg)`
  width: 21px;
  height: auto;
  opacity: 0;

  & rect {
    fill: ${props => props.theme.line};
  }

  transition: all 0.3s ease-in-out;
`;

const OptionWrapper = styled.div`
  position: absolute;

  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  height: 0;
  border: 0 solid transparent;
  overflow: hidden;

  z-index: 10;

  margin-top: 40px;
  margin-left: -20px;

  border-radius: 8px;

  transition: all 0.2s ease-in-out;
`;

const Option = styled.div`
  display: flex;
  flex-direction: column;;

  justify-content: center;
  align-items: center;

  width: 58px;
  height: 38px;

  margin: 0 0 0 0;

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.primary};
  background-color: ${props => props.theme.subBackground};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    color: ${props => props.theme.white};
    background-color: ${props => props.theme.primary};
  }
`;

const SubBackgroundCover = styled.div`
  position: relative;

  width: 1018px;
  height: 50px;

  margin-top: -50px;

  z-index: ${props => props.zIndex};

  border-radius: 30px;
  background-color: ${props => props.theme.background};
`;

const ExpertComponentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;

  z-index: ${props => props.zIndex};
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 24px;
  height: 24px;

  opacity: 0;

  &:hover {
    cursor: pointer;
  }

  transition: all 0.3s ease-in-out;
`;

const Icon = styled.img`
  width: 100%;
  height: 100%;
`;
